import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Reveal, RevealLines } from "@/components/motion/Reveal";
import Marquee from "@/components/Marquee";
import EarlyAccessButton from "@/components/EarlyAccessButton";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import { posts } from "@/data/journal";

const IMG = {
  hero: "https://images.unsplash.com/photo-1534171472159-edb6d1e0b63c?w=1800&q=85",
  skin: "https://images.unsplash.com/photo-1710580889701-9fa8f2cd5927?w=1400&q=85",
  ingredients: "https://images.unsplash.com/photo-1604304194650-3ba3cfa752fd?w=1400&q=85",
  powder: "https://images.unsplash.com/photo-1515688594390-b649af70d282?w=1400&q=85",
  brass: "https://images.unsplash.com/photo-1737016765829-5f8e43b2cbe3?w=1400&q=85",
};

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/l6t12iov_copy_B6D87D2D-73BE-4DD1-8251-9D24B19A048F.MOV";

export default function Home() {
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div data-testid="home-page">
      {/* ============================= HERO ============================= */}
      <section ref={heroRef} data-testid="home-hero" className="relative min-h-[92vh] w-full overflow-hidden bg-ink text-ivory">
        {/* Media layer — looping video */}
        <motion.div
          className="absolute inset-0"
          style={reduced ? {} : { y, scale }}
        >
          <video
            key="hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.85 }}
            poster="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/f96pa1gb_Model2.png"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src={HERO_VIDEO} type="video/quicktime" />
          </video>
          <div className="absolute inset-0 bg-ink/30 mix-blend-multiply" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/70 to-transparent" />
        </motion.div>

        {/* Top eyebrow */}
        <div className="absolute top-24 md:top-28 left-0 right-0 z-10">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex justify-between items-center">
            <p className="eyebrow text-ivory/85">Personal care for the exposed life</p>
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-[24vh] md:pt-[26vh] pb-24">
          <h1 className="font-display text-ivory leading-[1.14]" style={{ fontSize: "clamp(52px, 9vw, 156px)", fontWeight: 300 }}>
            <span className="block reveal-mask">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block"
              >
                Function,
              </motion.span>
            </span>
            <span className="block reveal-mask" style={{ paddingBottom: "0.14em" }}>
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block italic"
              >
                refined.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 max-w-xl text-ivory/85 text-[15px] leading-relaxed"
          >
            A small, deliberate house of personal care. Each product does one thing exceptionally,
            carried in fragrance and worn like something you chose.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <EarlyAccessButton label="Request early access" data-testid="hero-cta" />
            <Link to="/underbloom" data-testid="hero-secondary-link" className="text-ivory/90 text-[12px] tracking-[0.2em] uppercase link-underline">
              Discover Underbloom
            </Link>
          </motion.div>
        </div>

        {/* Bottom stamp */}
        <div className="absolute bottom-6 right-6 md:right-10 z-10 flex flex-col md:flex-row md:items-center gap-1 md:gap-6 text-right md:text-left">
          <p className="font-mono-stamp text-ivory/60">Whisper — The First Note</p>
          <p className="font-mono-stamp text-ivory/60">Underbloom · Arriving 2027</p>
        </div>
      </section>

      {/* ============================= BELIEF / MANIFESTO ============================= */}
      <section data-testid="home-belief" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter I</p>
              <p className="eyebrow eyebrow-brass mt-3">About Veylle</p>
            </Reveal>
          </div>
          <div className="md:col-span-9">
            <Reveal>
              <p className="font-display text-3xl md:text-5xl leading-[1.32]" style={{ fontWeight: 300 }}>
                We believe the things worn closest to skin, through heat and travel and long days,
                deserve the care usually saved for <em className="italic">fine fragrance.</em>
                {" "}That protection and comfort can feel beautiful, not clinical.
                Always gentle, never heavy. Made to be kept, not replaced.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 md:mt-14 text-pencil text-[15px] max-w-2xl leading-relaxed">
                Veylle is a house, not a single product. A small, deliberate collection of functional things,
                each refined until it feels like luxury, each carrying one quiet fragrance sensibility,
                so the whole reads as worn from the same hand. We build slowly, and we build to keep.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================= FOUNDER NOTE ============================= */}
      <section data-testid="home-founder" className="bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter II</p>
              <p className="eyebrow eyebrow-brass mt-3">A Note from the Founder</p>
              <div className="mt-10 border border-ink/10">
                <img src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/blg1n2ah_Color%20Logos.PNG" alt="Robin, founder of Veylle" className="w-full h-[620px] object-cover" loading="lazy" />
              </div>
              <p className="mt-6 font-display italic text-3xl">Robin</p>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-[92px]">
            <Reveal>
              <div className="space-y-6 text-[16px] leading-[1.85] text-ink">
                <p>I didn't set out to find a gap in a market. I kept finding gaps in my own days.</p>
                <p>
                  The products I used every day, the ones closest to skin, were the ones I never actually wanted to use.
                  What existed worked, technically. But it was harsh, or awkward, or something I would never choose to wear,
                  so I set it down and did without.
                </p>
                <p>
                  Underbloom is the clearest example. I run warm, and there is a particular self-consciousness in feeling
                  your own skin turn damp, aware of it before anyone else could be. Nothing on the shelf answered that in a
                  way that felt considered rather than clinical. So I made the thing I wanted: invisible, silky, quietly scented,
                  something I would reach for without thinking.
                </p>
                <p>
                  That became the whole idea. Nothing here is accidental, and nothing ships until it earns its place.
                  Veylle exists to keep closing those small gaps, the damp skin, the thing you won't wear, the fix that never
                  quite works, until the overlooked, uncomfortable moments of a day feel quietly handled. Made beautifully enough
                  to leave out, never hidden away.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================= UNDERBLOOM FEATURE ============================= */}
      <section data-testid="home-underbloom" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter III</p>
              <p className="eyebrow eyebrow-brass mt-3">Our First Product, Arriving 2027</p>
              <p className="font-display text-2xl mt-8" style={{ fontWeight: 400 }}>Underbloom</p>
              <h2 className="font-display text-6xl md:text-7xl mt-2 leading-[1.12]" style={{ fontWeight: 300 }}>
                Finish, <em className="italic">refined.</em>
              </h2>
              <p className="mt-6 font-display italic text-2xl text-pencil">The finish that disappears.</p>
              <p className="mt-8 text-[15px] leading-relaxed text-ink max-w-lg">
                A talc-free, refillable finishing powder for warm skin and the places it folds.
                Invisible, silky, quietly scented. Our first product, in development now.
              </p>
              <div className="mt-10">
                <Link to="/underbloom" data-testid="home-underbloom-link" className="btn-ghost">
                  Discover Underbloom →
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <Reveal delay={0.1}>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/ic9dn3oy_Underbloom%20Package%20Blur.png"
                  alt="Underbloom refillable compact"
                  className="w-full h-[520px] md:h-[620px] object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 font-mono-stamp text-pencil bg-ivory/85 px-2 py-1">
                  Preliminary design · Subject to change
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================= IN THE MATERIAL — hidden for now =============================
      <section data-testid="home-material" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-4">
              <p className="eyebrow eyebrow-brass mb-3">In the Material</p>
              <h3 className="font-display text-5xl md:text-6xl leading-[1.12]" style={{ fontWeight: 300 }}>
                Proof, <em className="italic">not gloss.</em>
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: IMG.powder, cap: "Powder — the disappearing finish", stamp: "001" },
              { img: IMG.ingredients, cap: "Ambrette · warm skin", stamp: "002" },
              { img: IMG.skin, cap: "Orris · white musk", stamp: "003" },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="relative overflow-hidden border border-sage">
                  <img src={t.img} alt={t.cap} className="w-full h-[420px] md:h-[520px] object-cover transition-transform duration-[1200ms] hover:scale-105" loading="lazy" />
                  <div className="absolute top-3 left-3 font-mono-stamp text-ivory/90 bg-ink/60 px-2 py-1">{t.stamp}</div>
                </div>
                <p className="mt-3 font-mono-stamp text-pencil">{t.cap}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* ============================= FRAGRANCE SPINE ============================= */}
      <section data-testid="home-fragrance" className="bg-ink text-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <div className="border border-ivory/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/xc6mflwe_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2011_00_12%20PM.png"
                  alt="Whisper — ambrette, orris, amber"
                  className="w-full h-[500px] object-cover"
                  style={{ objectPosition: "70% center" }}
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pl-10">
            <Reveal>
              <p className="eyebrow" style={{ color: "#AFA268" }}>The Fragrance Spine</p>
              <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[1.12]" style={{ fontWeight: 300 }}>
                It begins with <em className="italic">Whisper,</em>
              </h2>
              <p className="mt-6 font-display italic text-2xl text-ivory/70">Dry Skin-Musk Vanilla</p>
              <p className="mt-8 text-[15px] leading-relaxed text-ivory/85 max-w-xl">
                One scent, made properly. Ambrette and warm skin at the top, orris and clean white musk at the heart,
                vanilla kept low and dry in the base. Expensive skin, never dessert. It sits beneath your own fragrance
                and flatters it, never competes.
              </p>
              <p className="mt-6 font-display italic text-xl text-ivory/70 max-w-md">
                One scent to open the house. A wardrobe to follow, as the house grows.
              </p>
              <div className="mt-10">
                <Link to="/fragrance" data-testid="home-fragrance-link" className="text-ivory text-[12px] tracking-[0.2em] uppercase link-underline">
                  Read the fragrance →
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================= HOW WE BUILD ============================= */}
      <section data-testid="home-how" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <Reveal>
                <p className="eyebrow eyebrow-brass mb-4">How We Build</p>
                <h3 className="font-display text-5xl md:text-6xl leading-[1.12]" style={{ fontWeight: 300 }}>
                  In development, <em className="italic">in the open.</em>
                </h3>
                <p className="mt-8 max-w-md text-pencil text-[15px] leading-relaxed">
                  Nothing here is rushed and nothing ships until it earns its place. Follow the work as it happens.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 md:divide-x md:divide-sage border-t md:border-t-0 border-sage">
              {[
                { mark: "01", label: "Talc-free." },
                { mark: "02", label: "Refillable — made to keep." },
                { mark: "03", label: "Considered in small batches." },
              ].map((m, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="px-0 md:px-8 py-10 md:py-0 md:pt-4 md:pb-4">
                    <p className="font-mono-stamp text-brass">{m.mark}</p>
                    <p className="mt-4 font-display text-3xl md:text-4xl leading-tight" style={{ fontWeight: 400 }}>
                      {m.label}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================= JOURNAL TEASER ============================= */}
      <section data-testid="home-journal" className="bg-ivory border-t border-sage">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <p className="eyebrow eyebrow-brass mb-3">Journal</p>
              <h3 className="font-display text-5xl md:text-6xl leading-[1.12]" style={{ fontWeight: 300 }}>
                Letters from the <em className="italic">house.</em>
              </h3>
              <p className="mt-6 max-w-lg text-pencil text-[15px]">
                Notes from a house still being built. No noise, only what's worth writing down.
              </p>
            </div>
            <Link to="/journal" data-testid="home-journal-link" className="btn-ghost">
              Read the journal →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/journal/${p.slug}`} data-testid={`home-journal-card-${p.slug}`} className="block group">
                  <div className="overflow-hidden border border-sage">
                    <img src={p.hero + "?w=800&q=80"} alt={p.title} className="w-full h-[320px] object-cover transition-transform duration-[1200ms] group-hover:scale-105" loading="lazy" />
                  </div>
                  <p className="mt-4 font-mono-stamp text-pencil">{p.number} · {p.date}</p>
                  <p className="mt-2 font-display text-2xl md:text-3xl leading-tight" style={{ fontWeight: 400 }}>
                    {p.title}
                  </p>
                  <p className="mt-2 text-pencil text-sm">{p.dek}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
